import { Form, Formik, FormikProps } from 'formik';
import { Trans, useTranslation } from 'react-i18next';
import { Row, Col } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { formatCurrency } from 'common/services/currency';

import { Typography, QuestionWrapper, Tooltip } from '../../components';
import {
  RadioField,
  TextareaField,
  CheckBoxField,
  CheckBoxGroupField,
  InputNumberField,
  InputField,
  CheckboxTreeField,
} from '../../fields';
import { InvestingGoalsFormValues, HazardProfileFormValues } from '../../models/Forms';
import { I18nNameSpaces } from '../../services/i18n';
import { economicBackgroundCheckboxTree } from './templates';
import { makeInvestingGoalsSchema } from './schema';

interface Props {
  initialValues?: Partial<InvestingGoalsFormValues>;
  hazard?: HazardProfileFormValues;
  bindSubmit?: (formik: FormikProps<InvestingGoalsFormValues>) => void;
  onSubmit: (values: InvestingGoalsFormValues) => void;
}

export default function InvestingGoalsForm({ onSubmit, initialValues, bindSubmit, hazard }: Props) {
  const { t, i18n } = useTranslation(I18nNameSpaces.Forms);

  const schema = makeInvestingGoalsSchema(t);

  function handleOnSubmit(values: InvestingGoalsFormValues) {
    onSubmit && onSubmit(values);
  }

  function renderFinanceBackgroundTooltip() {
    return (
      i18n.language === 'cz' && (
        <Tooltip
          title={
            <div className="finance-background-tooltip__content">
              <Typography.Paragraph>{t('investingGoalsForm.g.labelTooltip1')}</Typography.Paragraph>
              <Typography.Paragraph>{t('investingGoalsForm.g.labelTooltip2')}</Typography.Paragraph>
              <Typography.Paragraph>
                <Trans
                  t={t}
                  i18nKey="investingGoalsForm.g.labelTooltip3"
                  components={[<Typography.Text underline key={0} />]}
                />
              </Typography.Paragraph>
            </div>
          }
        >
          <InfoCircleOutlined className="finance-background-tooltip" />
        </Tooltip>
      )
    );
  }

  function renderForm(formik: FormikProps<InvestingGoalsFormValues>) {
    if (bindSubmit) {
      bindSubmit(formik);
    }
    const { values } = formik;
    const hazardTimeHorizonSuffix = hazard?.d ? t(`hazardProfileForm.d.options.${hazard.d}`) : '';

    return (
      <div className="company-form">
        <Form>
          <QuestionWrapper>
            <QuestionWrapper.Divider>
              <QuestionWrapper.Title>
                {t('investingGoalsForm.timeHorizont')}
                {hazardTimeHorizonSuffix ? ` - ${hazardTimeHorizonSuffix}` : ''}
              </QuestionWrapper.Title>
            </QuestionWrapper.Divider>
            <QuestionWrapper.Divider>
              <QuestionWrapper.Title>{t('investingGoalsForm.a.label')}</QuestionWrapper.Title>
              <CheckBoxGroupField
                name="a"
                type="vertical"
                disabled
                options={[
                  ...['a', 'b', 'j', 'd', 'k', 'i'].map((value) => ({
                    label: t(`investingGoalsForm.a.options.${value}`),
                    value,
                  })),
                  {
                    label: t('investingGoalsForm.a.options.f'),
                    value: 'f',
                    custom: true,
                  },
                  {
                    label: null,
                    value: 'g', // Wealth conservation
                    legacy: true,
                  },
                  {
                    label: null,
                    value: 'h', // Regular income
                    legacy: true,
                  },
                ]}
              />
            </QuestionWrapper.Divider>
            <QuestionWrapper.Divider>
              <QuestionWrapper.Title>{t('investingGoalsForm.b.label')}</QuestionWrapper.Title>
              <TextareaField name="b" />
            </QuestionWrapper.Divider>
          </QuestionWrapper>
          <QuestionWrapper>
            <QuestionWrapper.Divider>
              <QuestionWrapper.Title>
                {t('investingGoalsForm.g.label')}
                {renderFinanceBackgroundTooltip()}
              </QuestionWrapper.Title>
            </QuestionWrapper.Divider>
            <QuestionWrapper.Divider>
              <CheckboxTreeField name="g" options={economicBackgroundCheckboxTree} />
            </QuestionWrapper.Divider>
            <QuestionWrapper.Divider>
              <QuestionWrapper.Title>{t('investingGoalsForm.d.label')}</QuestionWrapper.Title>
              <RadioField
                name="d.regularity"
                type="horizontal"
                options={[
                  {
                    label: t('investingGoalsForm.d.options.a'),
                    value: 'a',
                  },
                  {
                    label: t('investingGoalsForm.d.options.b'),
                    value: 'b',
                  },
                ]}
              />
              <Row align="middle" gutter={30}>
                <Col xs={24} md={12}>
                  <InputNumberField label={t('investingGoalsForm.d.regularly')} name="d.regularly" min={0} />
                </Col>
                <Col xs={24} md={12}>
                  <Typography.Text>{formatCurrency(values.d.regularly)}</Typography.Text>
                </Col>
                <Col xs={24} md={12}>
                  <InputNumberField label={t('investingGoalsForm.d.amount')} name="d.amount" min={0} />
                </Col>
                <Col xs={24} md={12}>
                  <Typography.Text>{formatCurrency(values.d.amount)}</Typography.Text>
                </Col>
              </Row>
            </QuestionWrapper.Divider>
            <QuestionWrapper.Divider>
              <RadioField
                name="e"
                type="vertical"
                options={[
                  {
                    label: t('investingGoalsForm.e.options.a'),
                    value: 'a',
                  },
                  {
                    label: t('investingGoalsForm.e.options.b'),
                    value: 'b',
                  },
                ]}
              />
            </QuestionWrapper.Divider>
          </QuestionWrapper>
          <QuestionWrapper>
            <QuestionWrapper.Divider>
              <CheckBoxField name="f.is" label={t('investingGoalsForm.f.label')} />
              {values.f.is ? (
                <>
                  <CheckBoxGroupField
                    label={t('investingGoalsForm.f.sub.a.label')}
                    name="f.a"
                    type="vertical"
                    options={[
                      {
                        label: t('investingGoalsForm.f.sub.a.options.a'),
                        value: 'a',
                      },
                      {
                        label: t('investingGoalsForm.f.sub.a.options.b'),
                        value: 'b',
                      },
                      {
                        label: t('investingGoalsForm.f.sub.a.options.c'),
                        value: 'c',
                      },
                      {
                        label: t('investingGoalsForm.f.sub.a.options.d'),
                        value: 'd',
                      },
                    ]}
                  />
                  <Typography.Paragraph>{t('investingGoalsForm.f.info.p1')}</Typography.Paragraph>
                  <Typography.Paragraph>{t('investingGoalsForm.f.info.p2a')}</Typography.Paragraph>
                  <Typography.Paragraph>{t('investingGoalsForm.f.info.p2b')}</Typography.Paragraph>
                  <Typography.Paragraph>{t('investingGoalsForm.f.info.p2c')}</Typography.Paragraph>
                  <Typography.Paragraph>{t('investingGoalsForm.f.info.p2d')}</Typography.Paragraph>
                  <Row gutter={20}>
                    <Col xs={24} md={12}>
                      <InputField name="f.b" label={t('investingGoalsForm.f.sub.b.label')} />
                    </Col>
                    <Col xs={24} md={12}>
                      <InputField name="f.c" label={t('investingGoalsForm.f.sub.c.label')} />
                    </Col>
                  </Row>
                </>
              ) : null}
            </QuestionWrapper.Divider>
          </QuestionWrapper>
        </Form>
      </div>
    );
  }

  return (
    <Formik
      initialValues={{
        a: initialValues?.a ?? [],
        b: initialValues?.b ?? '',
        d: {
          amount: initialValues?.d?.amount ?? 0,
          regularity: initialValues?.d?.regularity ?? '',
          regularly: initialValues?.d?.regularly ?? 0,
        },
        e: initialValues?.e ?? '',
        f: initialValues?.f ?? {
          is: false,
          a: [],
          b: '',
          c: '',
        },
        g: initialValues?.g ?? {},
      }}
      onSubmit={handleOnSubmit}
      validationSchema={schema}
      validateOnBlur={false}
      validateOnChange={false}
    >
      {renderForm}
    </Formik>
  );
}
